import { useEffect, useRef } from 'react';
import { useScopedRestaurant } from '../hooks/restaurantViewScope';
import { useOrderHistory } from '../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../stores/appRevenueHistoryStore';
import { usePriceAlertsStore } from '../stores/appPriceAlertsStore';
import { useWasteNeedsStore } from '../stores/appWasteNeedsStore';
import { useWasteStore } from '../stores/appWasteStore';
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../stores/appUserDefaultStore';
import { useRestaurantStore } from '../stores/appRestaurantStore';
import { useCostPriceStore } from '../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../stores/appCostPriceCategoriesStore';
import { useSubrecipesStore } from '../stores/appSubrecipesStore';
import { useRecipeStore } from '../stores/appRecipeStore';
import { useTeamMembersStore } from '../stores/appTeamMembersStore';
import { useStaffStore } from '../stores/appStaffStore';
import { useMenusStore } from '../stores/appMenusStore';
import { useInventoryStore } from '../stores/appInventoryStore';
import { useInventoryOverviewStore } from '../stores/appInventoryOverviewStore';
import { useAuth } from '../services/authentication/authContext';
import type { AccountProductFavoritesScope } from '../services/restaurant/restaurantService';

/** Refresh the shared list when entering All restaurants or returning to the tab. */
export function useLoadAccountProductFavorites(enabled: boolean, scope: AccountProductFavoritesScope = 'products'): string {
  const { backendUser, accountSettings } = useAuth();
  const load = useRestaurantStore(s => s.loadAccountFavorites);
  // Demo and setup-review sessions do not grant account-wide preference writes.
  const context = backendUser && !backendUser.demoContext && !accountSettings.includes('account_setup')
    ? `${backendUser.id}:${backendUser.created_by ?? backendUser.id}` : '';
  useEffect(() => {
    if (!enabled || !context) return;
    const refresh = () => { void load(context, scope); };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [enabled, context, load, scope]);
  return context;
}

/**
 * Custom hook to automatically load order history data for the selected restaurant.
 * Handles both single restaurant and "All Restaurants" scenarios.
 * Prevents duplicate loads using a ref-based tracking system.
 *
 * When `options.progressive` is true (used by the dashboard), the initial fetch is
 * scoped to a recent date window for a fast first render and the rest of the history
 * streams in in the background. `options.viewStart` lets the initial window cover the
 * period the user is currently looking at. All other callers fall back to the original
 * single full fetch, so pages that need the complete dataset are unaffected.
 */
export function useLoadOrderHistory(options?: { progressive?: boolean; viewStart?: Date }) {
  const orderHistoryStore = useOrderHistory();
  const selectedRestaurant = useScopedRestaurant();
  const restaurantId = selectedRestaurant?.id;
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);
  const platformMap = useRestaurantStore(s => s.restaurantSectionPlatforms);
  const restaurantDataLoaded = useRestaurantStore(s => s.isDataLoaded);
  const { backendUser } = useAuth();

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const orderHistoryLoadRequestedRef = useRef<string>('');

  // Keep the latest progressive options without retriggering the load effect.
  const progressive = !!options?.progressive;
  const viewStartRef = useRef<Date | undefined>(options?.viewStart);
  viewStartRef.current = options?.viewStart;

  useEffect(() => {
    if (!restaurantId) return;

    // Get sections for the selected restaurant
    // For "All Restaurants", get sections from all restaurants
    const sections = isAllRestaurants
      ? Object.values(restaurantSectionsMap).flat()
      : restaurantSectionsMap[restaurantId] || [];

    const platformIds = sections.flatMap(s => {
      const platforms = platformMap[s.id] || [];
      return platforms.map(p => p.id);
    }).sort();

    if (platformIds.length === 0) {
      // No platforms for this restaurant. Wait until the restaurant store has finished
      // loading before concluding it's genuinely empty (otherwise we'd flag a restaurant
      // empty while its sections/platforms are still loading). Once confirmed empty, mark
      // the store fully-loaded-empty so consumers render 0 instead of spinning forever.
      if (restaurantDataLoaded && !isAllRestaurants &&
          !(orderHistoryStore.currentRestaurantId === restaurantId && orderHistoryStore.fullyLoaded)) {
        orderHistoryStore.markOrderhistoryEmptyForRestaurant(restaurantId);
      } else if (orderHistoryStore.currentRestaurantId !== restaurantId) {
        // Prevent stale data from a previous restaurant from leaking into this one.
        orderHistoryStore.clearOrderhistoryDataForRestaurant(restaurantId);
      }
      return;
    }

    const requestKey = `${restaurantId}-${platformIds.join(',')}`;
    if (orderHistoryLoadRequestedRef.current === requestKey) return;

    if (!orderHistoryStore.isDataLoadedForPlatforms(platformIds)) {
      orderHistoryLoadRequestedRef.current = requestKey;
      if (progressive) {
        orderHistoryStore.loadByPlatformIdsProgressive(restaurantId, platformIds, platformToSectionIndex, backendUser, viewStartRef.current);
      } else {
        orderHistoryStore.loadByPlatformIds(restaurantId, platformIds, platformToSectionIndex, backendUser);
      }
      orderHistoryLoadRequestedRef.current = ""
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, progressive, restaurantDataLoaded]);

  // When the user changes the viewed period, prioritise fetching back to its start so
  // the selected window shows as fast as possible (the rest keeps streaming in the bg).
  const viewStartTime = options?.viewStart?.getTime();
  useEffect(() => {
    if (!progressive || !restaurantId || !viewStartRef.current) return;
    orderHistoryStore.ensureViewLoaded(viewStartRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewStartTime, progressive, restaurantId]);
}

/**
 * Custom hook to automatically load revenue history data for the selected restaurant.
 * Handles both single restaurant and "All Restaurants" scenarios.
 * Prevents duplicate loads using a ref-based tracking system.
 *
 * When `options.progressive` is true (used by the dashboard), the initial fetch is
 * scoped to a recent date window for a fast first render and the rest of the history
 * streams in in the background. `options.viewStart` lets the initial window cover the
 * period the user is currently looking at. All other callers fall back to the original
 * single full fetch, so pages that need the complete dataset are unaffected.
 */
export function useLoadRevenueHistory(options?: { progressive?: boolean; viewStart?: Date }) {
  const revenueHistoryStore = useRevenueHistory();
  const selectedRestaurant = useScopedRestaurant();
  const restaurantId = selectedRestaurant?.id;
  const revenueSourcesMap = useRestaurantStore(s => s.restaurantRevenueSources);
  const restaurantDataLoaded = useRestaurantStore(s => s.isDataLoaded);

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const revenueHistoryLoadRequestedRef = useRef<string>('');

  // Keep the latest progressive options without retriggering the load effect.
  const progressive = !!options?.progressive;
  const viewStartRef = useRef<Date | undefined>(options?.viewStart);
  viewStartRef.current = options?.viewStart;

  useEffect(() => {
    if (!restaurantId) return;

    // Get revenue sources for the selected restaurant
    // For "All Restaurants", get revenue sources from all restaurants
    const revenueSourceIds = isAllRestaurants
      ? Object.values(revenueSourcesMap).flat().map(rs => rs.id).sort()
      : (revenueSourcesMap[restaurantId] || []).map(rs => rs.id).sort();

    if (revenueSourceIds.length === 0) {
      // No revenue sources for this restaurant. Wait until the restaurant store has
      // finished loading before concluding it's genuinely empty (otherwise we'd flag a
      // restaurant empty while its sources are still loading). Once confirmed empty, mark
      // the store fully-loaded-empty so consumers render 0 instead of spinning forever.
      if (restaurantDataLoaded && !isAllRestaurants &&
          !(revenueHistoryStore.currentRestaurantId === restaurantId && revenueHistoryStore.fullyLoaded)) {
        revenueHistoryStore.markRevenuehistoryEmptyForRestaurant(restaurantId);
      } else if (revenueHistoryStore.currentRestaurantId !== restaurantId) {
        // Prevent stale data from a previous restaurant from leaking into this one.
        revenueHistoryStore.clearRevenuehistoryDataForRestaurant(restaurantId);
      }
      return;
    }

    const requestKey = `${restaurantId}-${revenueSourceIds.join(',')}`;

    if (revenueHistoryLoadRequestedRef.current === requestKey) return;

    if (!revenueHistoryStore.isDataLoadedForRevenueSources(revenueSourceIds)) {
      revenueHistoryLoadRequestedRef.current = requestKey;
      if (progressive) {
        revenueHistoryStore.loadByRevenueSourceIdsProgressive(restaurantId, revenueSourceIds, revenueSourcesMap, viewStartRef.current);
      } else {
        revenueHistoryStore.loadByRevenueSourceIds(restaurantId, revenueSourceIds, revenueSourcesMap);
      }
      revenueHistoryLoadRequestedRef.current = ""
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, progressive, restaurantDataLoaded]);

  // When the user changes the viewed period, prioritise fetching back to its start so
  // the selected window shows as fast as possible (the rest keeps streaming in the bg).
  const viewStartTime = options?.viewStart?.getTime();
  useEffect(() => {
    if (!progressive || !restaurantId || !viewStartRef.current) return;
    revenueHistoryStore.ensureViewLoaded(viewStartRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewStartTime, progressive, restaurantId]);
}

/**
 * Custom hook to automatically load price alerts data for the selected restaurant.
 * Handles both single restaurant and "All Restaurants" scenarios.
 * Prevents duplicate loads using a ref-based tracking system.
 */
export function useLoadPriceAlerts() {
  const priceAlertsStore = usePriceAlertsStore();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);
  const platformMap = useRestaurantStore(s => s.restaurantSectionPlatforms);

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const priceAlertsLoadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!restaurantId) return;

    // Get sections for the selected restaurant
    // For "All Restaurants", get sections from all restaurants
    const sections = isAllRestaurants
      ? Object.values(restaurantSectionsMap).flat()
      : restaurantSectionsMap[restaurantId] || [];

    const platforms = sections.flatMap(s => (platformMap[s.id] || []));
    const uniquePlatforms = Array.from(new Map(platforms.map(p => [p.id, p])).values());
    const platformIds = uniquePlatforms.map(p => p.id).sort();

    const requestKey = `${restaurantId}-${platformIds.join(',')}`;
    if (priceAlertsLoadRequestedRef.current === requestKey) return;

    if (platformIds.length > 0) {
      priceAlertsLoadRequestedRef.current = requestKey;
      priceAlertsStore.load(restaurantId, platformIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);
}

/**
 * Auto-load the per-restaurant "needed ingredients" index for the all-restaurants waste
 * card. Only runs in "All Restaurants" mode. The needs index is PERIOD-INDEPENDENT
 * (recipe expansion, not orders/revenue), so this is fetched ONCE — the Dashboard then
 * computes waste for the selected (and any) period entirely client-side from these needs
 * + already-loaded orders/revenue, so stepping the time controls hits no server.
 */
export function useLoadWasteNeeds() {
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const loadNeeds = useWasteNeedsStore(s => s.load);
  const wasteNeedsRequestedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isAllRestaurants) return;
    if (wasteNeedsRequestedRef.current) return;
    wasteNeedsRequestedRef.current = true;
    // Load every accessible restaurant's needs once; the dashboard filters client-side.
    loadNeeds({ restaurantIds: 'all' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllRestaurants]);
}

/**
 * Auto-load the logged waste entries for the selected restaurant. Single-restaurant only —
 * the waste list is per-restaurant, so "All Restaurants" loads nothing (its card reads the
 * needs index above instead).
 *
 * Shared by the waste page and the product-utilization report, which attributes these
 * entries to products via `computeWasteByProduct`.
 */
export function useLoadWaste() {
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const loadWaste = useWasteStore(s => s.load);

  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;
    loadWaste(restaurantId);
  }, [restaurantId, isAllRestaurants, loadWaste]);
}

/**
 * Custom hook to automatically load cost price data for the selected restaurant.
 * Only loads for single restaurants, not "All Restaurants".
 * Prevents duplicate loads using a ref-based tracking system.
 * Also loads custom ingredients needed for cost calculations.
 *
 * NOTE: This hook only loads cost price specific data. Order history and revenue history
 * should be loaded separately using useLoadOrderHistory() and useLoadRevenueHistory()
 * as they require platform/section information from the restaurant store.
 */
export function useLoadCostPrice() {
  const costPriceStore = useCostPriceStore();
  const categoriesStore = useCostPriceCategoriesStore();
  const selectedRestaurant = useScopedRestaurant();
  const restaurantId = selectedRestaurant?.id;

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const costPriceLoadRequestedRef = useRef<string>('');

  useEffect(() => {
    const loadCostPriceData = async () => {
      if (!restaurantId || isAllRestaurants) return;

      const requestKey = restaurantId;
      if (costPriceLoadRequestedRef.current === requestKey) return;

      costPriceLoadRequestedRef.current = requestKey;

      try {
        // Load cost price categories if not already loaded
        if (!categoriesStore.isDataLoadedFor(restaurantId)) {
          await categoriesStore.load(restaurantId);
        }

        // Load cost price data if not already loaded
        if (!costPriceStore.isDataLoadedFor(restaurantId)) {
          await costPriceStore.load(restaurantId);
        }

        // Load custom ingredients if not already loaded
        if (!costPriceStore.isCustomIngredientsLoadedFor(restaurantId)) {
          await costPriceStore.loadCustomIngredients(restaurantId);
        }
      } finally {
        costPriceLoadRequestedRef.current = '';
      }
    };

    loadCostPriceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);
}

/**
 * Custom hook to automatically load subrecipes for the selected restaurant.
 * Subrecipes are needed by the cost calc to resolve reference rows into live
 * ingredient lists. Single restaurant only — "All Restaurants" view doesn't
 * load subrecipes (cost calc skips subrecipe expansion for orphan references).
 */
export function useLoadSubrecipes() {
  const subrecipesStore = useSubrecipesStore();
  const selectedRestaurant = useScopedRestaurant();
  const restaurantId = selectedRestaurant?.id;

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const subrecipesLoadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;

    const requestKey = restaurantId;
    if (subrecipesLoadRequestedRef.current === requestKey) return;

    if (!subrecipesStore.isDataLoadedFor(restaurantId)) {
      subrecipesLoadRequestedRef.current = requestKey;
      subrecipesStore.load(restaurantId);
      subrecipesLoadRequestedRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);
}

/**
 * Custom hook to automatically load recipes list for the selected restaurant.
 * Prevents duplicate loads using a ref-based tracking system.
 */
export function useLoadRecipes() {
  const recipeStore = useRecipeStore();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const recipesLoadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;

    const requestKey = restaurantId;
    if (recipesLoadRequestedRef.current === requestKey) return;

    if (!recipeStore.isRecipesLoadedFor(restaurantId)) {
      recipesLoadRequestedRef.current = requestKey;
      recipeStore.loadRecipes(restaurantId);
      recipesLoadRequestedRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);
}

/**
 * Custom hook to automatically load recipe detail data (recipe + notes) for a specific recipe.
 * Both recipe and notes are always loaded/reloaded together.
 * Also loads team members for displaying user info on notes.
 * Prevents duplicate loads using a ref-based tracking system.
 */
export function useLoadRecipeDetail(recipeId: string | undefined) {
  const recipeStore = useRecipeStore();
  const teamMembersStore = useTeamMembersStore();
  const recipeDetailLoadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!recipeId) return;

    const requestKey = recipeId;
    if (recipeDetailLoadRequestedRef.current === requestKey) return;

    // Load team members if not already loaded (global data, load once)
    if (!teamMembersStore.isDataLoaded()) {
      teamMembersStore.load();
    }

    if (!recipeStore.isDetailLoadedFor(recipeId)) {
      recipeDetailLoadRequestedRef.current = requestKey;
      recipeStore.loadRecipeDetail(recipeId);
      recipeDetailLoadRequestedRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);
}

/**
 * Custom hook to automatically load menus for the selected restaurant.
 * Prevents duplicate loads using a ref-based tracking system.
 */
export function useLoadMenus() {
  const menusStore = useMenusStore();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;

  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const menusLoadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;

    const requestKey = restaurantId;
    if (menusLoadRequestedRef.current === requestKey) return;

    if (!menusStore.isDataLoadedFor(restaurantId)) {
      menusLoadRequestedRef.current = requestKey;
      menusStore.load(restaurantId);
      menusLoadRequestedRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);
}

/**
 * Custom hook to automatically load staff data.
 * Prevents duplicate loads using store's isDataLoaded flag.
 */
export function useLoadStaff() {
  const staffStore = useStaffStore();
  const staffLoadRequestedRef = useRef<boolean>(false);

  useEffect(() => {
    if (staffLoadRequestedRef.current) return;
    if (staffStore.isDataLoaded) return;

    staffLoadRequestedRef.current = true;
    staffStore.load();
    staffLoadRequestedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Auto-load inventory sections for the selected restaurant. Single restaurant only.
 */
export function useLoadInventorySections() {
  const inventoryStore = useInventoryStore();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const loadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;
    if (loadRequestedRef.current === restaurantId) return;
    if (inventoryStore.isSectionsLoadedFor(restaurantId)) return;
    loadRequestedRef.current = restaurantId;
    inventoryStore.loadSections(restaurantId);
    loadRequestedRef.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);
}

/**
 * Auto-load current inventory value summary for the selected restaurant.
 */
export function useLoadInventoryCurrentValue() {
  const inventoryStore = useInventoryStore();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const loadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;
    if (loadRequestedRef.current === restaurantId) return;
    // Already loaded for this restaurant — don't refetch on remount (would flip the
    // loading spinner and replace the data array, busting downstream report caches).
    if (inventoryStore.isCurrentValueLoadedFor(restaurantId)) return;
    loadRequestedRef.current = restaurantId;
    inventoryStore.loadCurrentValue(restaurantId);
    loadRequestedRef.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);
}

/**
 * Auto-load inventory counts history for the selected restaurant.
 */
export function useLoadInventoryCountsHistory() {
  const inventoryStore = useInventoryStore();
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const loadRequestedRef = useRef<string>('');

  useEffect(() => {
    if (!restaurantId || isAllRestaurants) return;
    if (loadRequestedRef.current === restaurantId) return;
    // Already loaded for this restaurant — don't refetch on remount (would flip the
    // loading spinner and replace the data array, busting downstream report caches).
    if (inventoryStore.isCountsLoadedFor(restaurantId)) return;
    loadRequestedRef.current = restaurantId;
    inventoryStore.loadCountsHistory(restaurantId);
    loadRequestedRef.current = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, isAllRestaurants]);
}

/**
 * Auto-load the all-restaurants inventory overview (latest count per restaurant × group +
 * the first page of the count feed). ONLY runs in "All Restaurants" mode — the normal
 * single-restaurant inventory page loads nothing from here.
 *
 * The store is reset when leaving all-restaurants mode so re-entering refetches: counts
 * land continuously from the Cloud Count app, and a stale board is worse than a refetch.
 */
export function useLoadInventoryOverview() {
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const load = useInventoryOverviewStore(s => s.load);
  const reset = useInventoryOverviewStore(s => s.reset);

  useEffect(() => {
    if (!isAllRestaurants) return;
    load();
    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllRestaurants]);
}
